import { useMemo, type ReactNode } from "react";
import { Badge } from "@project/components/ui/badge";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import {
  DollarSign,
  Download,
  FileText,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Sprout,
  User,
} from "lucide-react";
import {
  formatDonationDate,
  formatPaymentStatusLabel,
  paymentStatusBadgeClass,
} from "@project/types/donation-detail";
import {
  getGaushalaDonationAmount,
  getGaushalaDonationDonorName,
  getGaushalaDonationPhone,
  getGaushalaDonationReceiptNo,
  getGaushalaDonationSchemeLabel,
  getGaushalaDonationStatus,
  type GaushalaDonation,
  type GaushalaDonationPayment,
} from "@project/types/gaushala";

type GaushalaDonationDetailDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  donation: GaushalaDonation | null;
  loading: boolean;
  onDownloadReceipt: (donation: GaushalaDonation) => void;
};

export function GaushalaDonationDetailDialog({
  open,
  onOpenChange,
  donation,
  loading,
  onDownloadReceipt,
}: GaushalaDonationDetailDialogProps) {
  const paymentHistory = useMemo<GaushalaDonationPayment[]>(() => {
    if (!donation) {
      return [];
    }
    return donation.payments ?? [];
  }, [donation]);

  const status = donation ? getGaushalaDonationStatus(donation) : "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-xl">
        <DialogHeader className="border-b px-6 pb-4 pr-14 pt-5 text-left">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-3 text-lg font-semibold">
              <span className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-primary">
                <DollarSign className="h-5 w-5" />
              </span>
              Gaushala Donation Details
            </DialogTitle>
            {donation && !loading && (
              <Badge
                className={`capitalize ${paymentStatusBadgeClass(status)}`}
              >
                {formatPaymentStatusLabel(status)}
              </Badge>
            )}
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex h-[400px] flex-col items-center justify-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="animate-pulse font-medium text-muted-foreground">
              Fetching donation details...
            </p>
          </div>
        ) : donation ? (
          <>
            <div className="max-h-[min(80vh,730px)] overflow-y-auto">
              <div className="bg-primary/5 border-b px-6 py-5">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
                  <div className="min-w-0 space-y-1 sm:flex-1">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-primary">
                      Transaction amount
                    </p>
                    <p className="text-2xl font-semibold sm:text-3xl">
                      ₹
                      {getGaushalaDonationAmount(donation).toLocaleString(
                        "en-IN"
                      )}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-end gap-x-10 gap-y-4">
                    <SummaryItem
                      label="Date"
                      value={formatDonationDate(
                        donation.created_at ?? donation.donation_date
                      )}
                    />
                    <SummaryItem
                      label="Scheme"
                      value={getGaushalaDonationSchemeLabel(donation)}
                    />
                    <SummaryItem
                      label="Mode"
                      value={
                        donation.mode
                          ? donation.mode.charAt(0).toUpperCase() +
                            donation.mode.slice(1)
                          : "—"
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6 px-6 py-5 md:grid-cols-2">
                <InfoCard
                  title="Donor information"
                  icon={<User className="h-4 w-4" />}
                >
                  <DetailItem
                    icon={<User className="h-3.5 w-3.5" />}
                    label="Donor name"
                    value={getGaushalaDonationDonorName(donation)}
                  />
                  <DetailItem
                    icon={<User className="h-3.5 w-3.5" />}
                    label="Display name"
                    value={donation.display_name?.trim() ?? "—"}
                  />
                  <DetailItem
                    icon={<Mail className="h-3.5 w-3.5" />}
                    label="Email address"
                    value={donation.email?.trim() ?? "—"}
                  />
                  <DetailItem
                    icon={<Phone className="h-3.5 w-3.5" />}
                    label="Phone number"
                    value={getGaushalaDonationPhone(donation)}
                  />
                  <DetailItem
                    icon={<FileText className="h-3.5 w-3.5" />}
                    label="Receipt no"
                    value={getGaushalaDonationReceiptNo(donation)}
                  />
                </InfoCard>

                <InfoCard title="Address" icon={<MapPin className="h-4 w-4" />}>
                  <DetailItem
                    icon={<MapPin className="h-3.5 w-3.5" />}
                    label="Full address"
                    value={donation.full_address?.trim() ?? "—"}
                  />
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <DetailItem
                      icon={<MapPin className="h-3.5 w-3.5" />}
                      label="City"
                      value={donation.city?.trim() ?? "—"}
                    />
                    <DetailItem
                      icon={<MapPin className="h-3.5 w-3.5" />}
                      label="State"
                      value={donation.state?.trim() ?? "—"}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <DetailItem
                      icon={<MapPin className="h-3.5 w-3.5" />}
                      label="Pincode"
                      value={donation.pincode?.trim() ?? "—"}
                    />
                    <DetailItem
                      icon={<MapPin className="h-3.5 w-3.5" />}
                      label="Country"
                      value={donation.country?.trim() ?? "—"}
                    />
                  </div>
                </InfoCard>

                <div className="md:col-span-2">
                  <InfoCard
                    title="Donation details"
                    icon={<Sprout className="h-4 w-4" />}
                  >
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                      <DetailItem
                        icon={<Sprout className="h-3.5 w-3.5" />}
                        label="Scheme"
                        value={getGaushalaDonationSchemeLabel(donation)}
                      />
                      <DetailItem
                        icon={<FileText className="h-3.5 w-3.5" />}
                        label="Preferred Tithi"
                        value={donation.preferred_tithi?.trim() ?? "—"}
                      />
                      <DetailItem
                        icon={<FileText className="h-3.5 w-3.5" />}
                        label="Occasion"
                        value={donation.occasion?.trim() ?? "—"}
                      />
                      <DetailItem
                        icon={<FileText className="h-3.5 w-3.5" />}
                        label="Payment Mode"
                        value={donation.payment_mode?.trim() ?? "—"}
                      />
                    </div>
                  </InfoCard>
                </div>

                <div className="md:col-span-2">
                  <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold">
                    <span className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                      <MessageSquare className="h-4 w-4 text-primary" />
                    </span>
                    Special instructions
                  </h3>
                  <div className="border-y py-4 pl-4 pr-4">
                    <p className="border-l-[3px] border-primary pl-4 text-sm font-medium leading-relaxed text-muted-foreground">
                      &quot;
                      {donation.special_instructions?.trim() ??
                        donation.message?.trim() ??
                        "No special instructions provided with this donation."}
                      &quot;
                    </p>
                  </div>
                </div>

                <div className="space-y-3 md:col-span-2">
                  <h3 className="flex items-center gap-2.5 text-base font-bold">
                    <span className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md">
                      <FileText className="h-4 w-4 text-primary" />
                    </span>
                    Payment history
                  </h3>
                  <div className="overflow-x-auto rounded-lg border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Order ID</TableHead>
                          <TableHead>Payment ID</TableHead>
                          <TableHead>Method</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paymentHistory.length === 0 ? (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="py-6 text-center text-muted-foreground"
                            >
                              No payment records found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          paymentHistory.map(payment => (
                            <TableRow key={payment.id}>
                              <TableCell className="font-mono text-xs">
                                {payment.razorpay_order_id ?? "—"}
                              </TableCell>
                              <TableCell className="font-mono text-xs">
                                {payment.razorpay_payment_id ?? "—"}
                              </TableCell>
                              <TableCell className="capitalize">
                                {payment.payment_method ?? "—"}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  className={`capitalize ${paymentStatusBadgeClass(payment.status)}`}
                                >
                                  {formatPaymentStatusLabel(payment.status)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-bold">
                                ₹
                                {parseFloat(
                                  payment.amount || "0"
                                ).toLocaleString("en-IN")}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-3 border-t px-6 py-4 sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
              <Button
                type="button"
                className="gap-2"
                onClick={() => onDownloadReceipt(donation)}
              >
                <Download className="h-4 w-4" />
                Download receipt
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="truncate text-sm font-semibold" title={value}>
        {value}
      </p>
    </div>
  );
}

function InfoCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card p-5 shadow-sm">
      <h3 className="mb-4 flex items-center gap-2.5 text-base font-bold">
        <span className="bg-primary/10 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-primary">
          {icon}
        </span>
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
        <p className="break-words text-sm font-medium" title={value}>
          {value}
        </p>
      </div>
    </div>
  );
}
